import { CommandType } from '@shared/constants/CommandValues';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useHotkeyForCopyMacro } from '@shared/hooks/useHotkeyForCopyMacro';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import _ from 'lodash';
import { useState } from 'react';

/**
 * Encapsulates all macro list manipulation logic shared between
 * CreateOrUpdateProfilePage and OrgProfileEditPage.
 */
export function useMacroEditing() {
    const filter = useEditProfileStore((state) => state.filter);
    const hotkeys = useEditProfileStore((state) => state.hotkeys);
    const prepareHotkeysForCopyMacro = useHotkeyForCopyMacro();
    const [macroToAddCommand, setMacroToAddCommand] = useState<MacroDisplayModel | undefined>();
    const [editingMacroIndex, setEditingMacroIndex] = useState<number | undefined>();

    // ── Search ────────────────────────────────────────────────────────────────

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        useEditProfileStore.getState().setFilter(e.target.value);
    };

    const handleClearSearch = () => {
        useEditProfileStore.getState().setFilter('');
    };

    // ── Macro-level actions ───────────────────────────────────────────────────

    const copyMacro = (macro: MacroDisplayModel) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        const cloneMacro = _.cloneDeep(macro);
        const preparedCopy = prepareHotkeysForCopyMacro(cloneMacro, hotkeys);
        const updatedMacros = [{ ...preparedCopy.newMacro }, ...macros];
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    const handleRemoveMacro = (macro: MacroDisplayModel) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        macro.commands?.forEach((c) => {
            if (c.commandType === 'hotkey') {
                useEditProfileStore.getState().removeHotkeyFromMacro(c);
            }
        });
        const updatedMacros = macros.filter((h) => h !== macro);
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    const handleMacroChange = (updatedMacro: MacroDisplayModel) => {
        const macros = useEditProfileStore.getState().macros;
        const updatedMacros = macros.map((m) => (m.id === updatedMacro.id ? updatedMacro : m));
        useEditProfileStore.getState().updateMacros(updatedMacros);
        useEditProfileStore.getState().setHasChanges(true);
    };

    const handleNameChange = (text: string, macro: MacroDisplayModel) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        const updatedMacros = macros.map((m) => (m.id === macro.id ? { ...m, name: text } : m));
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    // ── Step actions ─────────────────────────────────────────────────────────

    const handleAddStep = (macroIndex: number) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        const updatedMacros = macros.map((m, i) => {
            if (i !== macroIndex) return m;
            return {
                ...m,
                events: [...m.events, { eventType: 'KeyPress' as const, keys: [''], holdTimeMilliseconds: 0 }],
            };
        });
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    // ── Command actions ───────────────────────────────────────────────────────

    const handleCommandChange = (value: string, macro: MacroDisplayModel, index: number) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        const originalValue = macro.commands[index].command;
        const commandType = macro.commands[index].commandType;
        let hotkeyLocalId = macro.commands[index].hotkeyLocalId;
        if (commandType === 'hotkey') {
            const hotkeyId = useEditProfileStore.getState().updateHotkeyFromMacro(value, macro, index, originalValue);
            hotkeyLocalId = hotkeyId ?? null;
        }
        const updatedMacros = macros.map((m) => {
            if (m.id !== macro.id) return m;
            return {
                ...m,
                commands: m.commands.map((c, i) => (i === index ? { ...c, command: value, hotkeyLocalId } : c)),
            };
        });
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    const handleRemoveCommand = (_command: string, macro: MacroDisplayModel, index: number) => {
        const macros = useEditProfileStore.getState().macros;
        useEditProfileStore.getState().setHasChanges(true);
        const c = macro.commands[index];
        const updatedMacros = macros.map((m) => {
            if (m.id !== macro.id) return m;
            return { ...m, commands: m.commands.filter((_, i) => i !== index) };
        });
        useEditProfileStore.getState().updateMacros(updatedMacros);
        if (c.commandType === 'hotkey') {
            useEditProfileStore.getState().removeHotkeyFromMacro(c);
        }
    };

    const handleAddCommand = (macro: MacroDisplayModel) => {
        useEditProfileStore.getState().setHasChanges(true);
        setMacroToAddCommand(macro);
    };

    const handleAddCommandWithType = (macro: MacroDisplayModel | undefined, commandType: CommandType) => {
        const macros = useEditProfileStore.getState().macros;
        setMacroToAddCommand(undefined);
        useEditProfileStore.getState().setHasChanges(true);
        if (!macro) return;
        const updatedMacros = macros.map((m) => {
            if (m.id !== macro.id) return m;
            return { ...m, commands: [...m.commands, { command: '', commandType }] };
        });
        useEditProfileStore.getState().updateMacros(updatedMacros);
    };

    return {
        // state
        filter: filter,
        macroToAddCommand,
        setMacroToAddCommand,
        editingMacroIndex,
        setEditingMacroIndex,
        // search
        handleSearchChange,
        handleClearSearch,
        // macro
        copyMacro,
        handleRemoveMacro,
        handleMacroChange,
        handleNameChange,
        // steps
        handleAddStep,
        // commands
        handleCommandChange,
        handleRemoveCommand,
        handleAddCommand,
        handleAddCommandWithType,
    };
}
